/* ====================================================================
   UserItem.jsx   (닉네임: 요약 모달,  포인트: 관리 모달)
   - 권한 변경 / 정지 / 삭제 → MUI Snackbar 알림
   - 날짜 포맷: YYYY-MM-DD HH:mm
   - 포인트: 천단위 콤마 + pt
==================================================================== */
import React, { useState } from 'react';
import {
  TableRow, TableCell, Button, Select, MenuItem,
  Stack, Typography, Snackbar, Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../../api/axios';
import UserDetailModal from './UserDetailModal';
import UserSummaryModal from './UserSummaryModal';

export default function UserItem({ user, userInfo, refreshUsers }) {
  /* ───── 권한 판별 ───── */
  const role = userInfo?.role;
  const isTargetMaster = user.role === 'master';
  const canBan =
    role === 'master' || (role === 'admin' && !isTargetMaster);
  const isMaster = role === 'master';

  /* 모달 상태 */
  const [openManage, setOpenManage] = useState(false);
  const [openSummary, setOpenSummary] = useState(false);

  /* ───── Snackbar 상태 ───── */
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'info', // success | error | warning | info
  });
  const showSnack = (message, severity = 'info') =>
    setSnack({ open: true, message, severity });
  const handleSnackClose = () => setSnack(s => ({ ...s, open: false }));

  /* ───── 액션 ───── */
  // 1) 역할 변경
  const changeRole = async (newRole) => {
    try {
      await api.post(`/api/management/users/${user.id}/role/`, { role: newRole });
      showSnack('권한이 변경되었습니다.', 'success');
      refreshUsers();
    } catch {
      showSnack('권한 변경 실패', 'error');
    }
  };

  // 2) 정지 / 해제
  const toggleBan = async () => {
    const confirmMsg = `${user.username} 계정을 ${user.is_banned ? '정지 해제' : '정지'}하시겠습니까?`;
    if (!window.confirm(confirmMsg)) return;

    const action = user.is_banned ? 'unban' : 'ban';
    try {
      await api.post(`/api/management/users/${user.id}/${action}/`);
      showSnack('상태가 변경되었습니다.', 'success');
      refreshUsers();
    } catch {
      showSnack('상태 변경 실패', 'error');
    }
  };

  // 3) 삭제 (마스터)
  const deleteUser = async () => {
    if (!window.confirm(`${user.username} 계정을 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/api/management/users/${user.id}/delete/`);
      showSnack('계정이 삭제되었습니다.', 'success');
      refreshUsers();
    } catch {
      showSnack('계정 삭제 실패', 'error');
    }
  };

  /* ───── 렌더 ───── */
  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: 'grey.100' },
          /* 모바일: 행 자체 90 % 축소 */
          '@media (max-width:600px)': {
            fontSize: '0.8rem',
          },
        }}
      >
        {/* 닉네임 → 요약 모달 */}
        <TableCell align="center">
          <Button
            variant="text"
            size="small"
            onClick={() => setOpenSummary(true)}
            sx={{
              p: 0,
              minWidth: 0,
              '@media (max-width:600px)': { fontSize: '0.8rem' },
            }}
          >
            {user.nickname || user.username}
          </Button>
        </TableCell>

        {/* 포인트 → 관리 모달 */}
        <TableCell align="center">
          <Button
            variant="text"
            size="small"
            onClick={() => setOpenManage(true)}
            sx={{
              p: 0,
              minWidth: 0,
              textTransform: 'none',
              '@media (max-width:600px)': { fontSize: '0.8rem' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user.points.toLocaleString()} Leaf🌿
            </Typography>
          </Button>
        </TableCell>

        {/* 가입일 */}
        <TableCell align="center">
          {dayjs(user.date_joined).format('YYYY-MM-DD')}
        </TableCell>

        {/* 최근 접속 */}
        <TableCell align="center">
          {user.last_login
            ? dayjs(user.last_login).format('YYYY-MM-DD')
            : '-'}
        </TableCell>

        {/* 상태 */}
        <TableCell align="center">
          {user.is_banned ? '정지' : '정상'}
        </TableCell>

        {/* 권한 */}
        <TableCell align="center" sx={{ minWidth: 130 }}>
          {isMaster ? (
            <Select
              value={user.role}
              size="small"
              onChange={(e) => changeRole(e.target.value)}
              sx={{
                '@media (max-width:600px)': { fontSize: '0.8rem', height: 32 },
              }}
            >
              <MenuItem value="user">유저</MenuItem>
              <MenuItem value="admin">어드민</MenuItem>
              <MenuItem value="master">마스터</MenuItem>
            </Select>
          ) : (
            user.role
          )}
        </TableCell>

        {/* 관리 버튼 */}
        <TableCell align="center">
          <Stack direction="row" spacing={1} justifyContent="center">
            {canBan && (
              <Button
                size="small"
                variant="contained"
                color={user.is_banned ? 'success' : 'error'}
                onClick={toggleBan}
                sx={{
                  '@media (max-width:600px)': { fontSize: '0.75rem', px: 1.2, py: 0.4 },
                }}
              >
                {user.is_banned ? '해제' : '정지'}
              </Button>
            )}
            {isMaster && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={deleteUser}
                sx={{
                  '@media (max-width:600px)': { fontSize: '0.75rem', px: 1.2, py: 0.4 },
                }}
              >
                삭제
              </Button>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* 닉네임 요약 모달 */}
      <UserSummaryModal
        open={openSummary}
        onClose={() => setOpenSummary(false)}
        user={user}
      />

      {/* 포인트 관리 모달 */}
      <UserDetailModal
        open={openManage}
        onClose={() => setOpenManage(false)}
        user={user}
        refreshUsers={refreshUsers}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
